import React, { FC, useCallback, useEffect, useState } from 'react';
import { useClearRefinements, useRange } from 'react-instantsearch';
import { useTranslation } from '~/shared/utils/translation';
import IRefinementList from './RefinementList.definitions';
import { RefinementSearchInput } from '~/features/productList/components/RefinementSearchInput';
import { FilterSelectionHeader } from '~/features/productList/components/FilterSelectionHeader';
import { StickyFilterHeader } from '~/features/productList/components/StickyFilterHeader';
import { RefinementListRow } from '~/features/productList/components/RefinementListRow';
import { VirtualizedList } from '~/features/productList/components/VirtualizedList';
import { useDebounce } from 'react-use';
import useOptimizedRefinementList from '~/shared/hooks/useOptimizedRefinementList';
import { usePinnedRefinementItems } from '~/features/productList/hooks/usePinnedRefinementItems';
import RefinementEmptyList from '../../FilterList/components/RefinementEmptyList';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';
import { Facets } from '~/services/instantsearch';
import { useScrollToTopOnSearch } from '~/features/productList/hooks/useScrollToTopOnSearch';

export const RefinementPriceList: FC<IRefinementList> = ({
    attribute,
    label,
    searchable,
    showCount,
    topContent,
    isActive = true,
}) => {
    const { translate } = useTranslation();
    const { trackFilter } = useDrawerFilterTracking();
    const [searchValue, setSearchValue] = useState('');

    useScrollToTopOnSearch(searchValue);

    const {
        refine,
        searchForItems,
        allItems: fetchedItems,
    } = useOptimizedRefinementList({
        attribute,
        escapeFacetValues: false,
    });
    const items = usePinnedRefinementItems(fetchedItems, searchValue, isActive);
    const { start, range } = useRange({ attribute: Facets.EFFECTIVE_PRICE });
    const { refine: clearSelection } = useClearRefinements({
        includedAttributes: [attribute, Facets.EFFECTIVE_PRICE],
    });
    // Slider/min-max inputs refine cust_effectivePrice separately from the checkboxes, so count them too.
    const isRangeRefined =
        (start[0] !== -Infinity && start[0] !== range.min) ||
        (start[1] !== Infinity && start[1] !== range.max);
    const selectedCount = items.filter((item) => item.isRefined).length + (isRangeRefined ? 1 : 0);

    const getItemLabel = useCallback(
        (itemLabel: string) => {
            switch (itemLabel) {
                case 'true':
                    return translate('pdp.spec.true');
                case 'false':
                    return translate('pdp.spec.false');
                default:
                    return itemLabel;
            }
        },
        [translate],
    );

    const handleRefine = useCallback(
        (value: string) => {
            if (!value) return;
            trackFilter(attribute, value, {
                isRefined: items.find((item) => item.value === value)?.isRefined,
            });

            refine(value);
        },
        [attribute, items],
    );

    useDebounce(
        () => {
            searchForItems(searchValue ?? '');
        },
        200,
        [searchValue],
    );

    useEffect(() => {
        if (searchValue) {
            searchForItems(searchValue ?? '');
        }
    }, [searchValue, items]);

    return (
        <>
            <StickyFilterHeader>
                {topContent}

                {searchable && (
                    <RefinementSearchInput
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        onClear={() => setSearchValue('')}
                        placeholder={translate('plp.searchPlaceholder')}
                    />
                )}

                <FilterSelectionHeader
                    label={label}
                    allLabel={translate('plp.facetSelection.price.all')}
                    selectedLabel={translate('plp.facetSelection.price.selected')}
                    selectedCount={selectedCount}
                    onClear={clearSelection}
                />
            </StickyFilterHeader>

            {items.length === 0 ? (
                <RefinementEmptyList />
            ) : (
                <VirtualizedList
                    isActive={isActive}
                    items={items}
                    itemKey={(item) => `${item.label}-${item.value}`}
                    renderItem={(item) => (
                        <RefinementListRow
                            label={getItemLabel(item.label)}
                            value={item.value}
                            count={item.count}
                            isRefined={item.isRefined}
                            shouldShowCount={showCount}
                            onRefine={handleRefine}
                        />
                    )}
                />
            )}
        </>
    );
};

export default RefinementPriceList;
